/*
  Leaflet.BeautifyIcon, a plugin that adds colorful iconic markers for Leaflet by giving full control of style to end user, It has also ability to adjust font awesome
  and glyphicon icons
  (c) 2016-2017, Muhammad Arslan Sajid
  http://leafletjs.com
*/

/**
 * beautifyMarker
 * テキストを埋め込んだマーカーの描写をサポートしてくれるプラグイン
 * GitHub
 * https://github.com/masajid390/BeautifyMarker
 * GitHubのソースコードと実装形式が異なる理由
 * GitHubでは、js形式でコーディングされているがMiiaFWでは、モジュールとして認識されないため、
 * ts形式に変更し実装
 */

import L, { type PointExpression } from 'leaflet'

type IconShape = 'circle' | 'marker' | 'circleDot' | 'rectangleDot' | 'doughnut'

export interface BeautifyIconOptions extends L.IconOptions {
  icon?: string
  iconSize?: PointExpression
  iconAnchor?: PointExpression
  iconShape?: IconShape
  iconStyle?: string
  innerIconAnchor?: PointExpression
  innerIconStyle?: string
  isAlphaNumericIcon?: boolean
  text?: string
  borderColor?: string
  borderWidth?: number
  borderStyle?: string
  backgroundColor?: string
  textColor?: string
  customClasses?: string
  spin?: boolean
  prefix?: string
}

type Points = {
  [k in IconShape]?: L.PointExpression
}

type BM = {
  iconColor: string
  iconAnchor: Points
  popupAnchor: Points
  innerIconAnchor: Points
  iconSize: Points
}

export const bm: BM = {
  iconColor: '#1EB300',
  iconAnchor: {
    marker: [14, 36],
    circle: [11, 10],
    circleDot: [5, 5],
    rectangleDot: [5, 6],
    doughnut: [8, 8]
  },
  popupAnchor: {
    marker: [0, -25],
    circle: [-3, -76],
    circleDot: [0, -2],
    rectangleDot: [0, -2],
    doughnut: [0, -2]
  },
  innerIconAnchor: {
    marker: [-2, 5],
    circle: [0, 2]
  },
  iconSize: {
    marker: [28, 28],
    circle: [22, 22],
    circleDot: [2, 2],
    rectangleDot: [2, 2],
    doughnut: [15, 15]
  }
}

export class BeautifyIcon extends L.Icon {
  readonly options: BeautifyIconOptions

  constructor(options: BeautifyIconOptions) {
    super(options)
    this.options = {
      icon: 'leaf',
      iconSize: bm.iconSize.circle,
      iconAnchor: bm.iconAnchor.circle,
      iconShape: 'circle',
      iconStyle: '',
      innerIconAnchor: [0, 3],
      innerIconStyle: '',
      isAlphaNumericIcon: false,
      text: '',
      borderColor: bm.iconColor,
      borderWidth: 2,
      borderStyle: 'solid',
      backgroundColor: 'white',
      textColor: bm.iconColor,
      customClasses: '',
      spin: false,
      prefix: 'fa',
      ...options
    }
  }

  public initialize(): void {
    this.applybm(this.options)
  }

  public applybm(options: BeautifyIconOptions): void {
    if (options) {
      if (!options.iconSize && options.iconShape) {
        options.iconSize = bm.iconSize[options.iconShape]
      }

      if (!options.iconAnchor && options.iconShape) {
        options.iconAnchor = bm.iconAnchor[options.iconShape]
      }

      if (!options.popupAnchor && options.iconShape) {
        options.popupAnchor = bm.popupAnchor[options.iconShape]
      }

      if (!options.innerIconAnchor) {
        if (options.iconShape === 'circle' || options.iconShape === 'marker') {
          if (options.iconShape === 'circle' && options.isAlphaNumericIcon) {
            options.innerIconAnchor = [0, -1]
          } else if (options.iconShape === 'marker' && !options.isAlphaNumericIcon) {
            options.innerIconAnchor = bm.innerIconAnchor[options.iconShape]
          }
        }
      }
    }
  }

  public createIcon(): HTMLElement {
    const iconDiv = document.createElement('div')
    iconDiv.innerHTML = this.createIconInnerHtml()

    this._setIconStyles(iconDiv)

    if (this.options.iconShape === 'marker') {
      const wrapperDiv = document.createElement('div')
      wrapperDiv.className = 'beautify-marker'
      wrapperDiv.appendChild(iconDiv)
      return wrapperDiv
    }

    return iconDiv
  }

  private createIconInnerHtml(): string {
    const options = this.options

    if (
      options.iconShape === 'circleDot' ||
      options.iconShape === 'rectangleDot' ||
      options.iconShape === 'doughnut'
    ) {
      return ''
    }

    const innerIconStyle = this.getInnerIconStyle(options)

    if (options.isAlphaNumericIcon) {
      return `<div style="${innerIconStyle}">${options.text}</div>`
    }

    const spinClass = options.spin ? ' fa-spin' : ''

    return `<i class="${options.prefix} ${options.prefix}-${options.icon}${spinClass}" style="${innerIconStyle}"></i>`
  }

  private getInnerIconStyle(options: BeautifyIconOptions): string {
    const innerAnchor = L.point(options.innerIconAnchor ?? [0, 0])
    return `color:${options.textColor};margin-top:${innerAnchor.y}px; margin-left:${innerAnchor.x}px;${options.innerIconStyle}`
  }

  private _setIconStyles(iconDiv: HTMLElement): void {
    const options = this.options
    const size = L.point(options.iconSize ?? [0, 0])
    const anchor = L.point(options.iconAnchor ?? [0, 0])

    iconDiv.className = 'beautify-marker'
    if (options.iconShape) {
      iconDiv.classList.add(options.iconShape)
    }

    if (options.customClasses) {
      iconDiv.className += ` ${options.customClasses}`
    }

    iconDiv.style.backgroundColor = options.backgroundColor ?? 'transparent'
    iconDiv.style.color = options.textColor ?? 'black'
    iconDiv.style.borderColor = options.borderColor ?? 'black'
    iconDiv.style.borderWidth = `${options.borderWidth}px`
    iconDiv.style.borderStyle = options.borderStyle ?? 'solid'
    iconDiv.style.width = `${size.x}px`
    iconDiv.style.height = `${size.y}px`
    iconDiv.style.marginLeft = `${-anchor.x}px`
    iconDiv.style.marginTop = `${-anchor.y}px`

    if (options.iconStyle) {
      iconDiv.setAttribute('style', `${iconDiv.getAttribute('style')}${options.iconStyle}`)
    }
  }
}
